import { ItemBar } from "components/ItemBar";
import { ToastContext } from "context/ToastContext";
import { useUserWithAccount } from "context/UserAccountProvider";
import { Button } from "primereact/button";
import { useContext, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  useUpdateAccountMutation,
  useUpsertPayerAccountFeesMutation,
  useUpsertBenefitSelectionRulesMutation,
} from "store/queries/account";

import { validaTeCustomPaymentPlanSettings } from "../helpers/helpers";
import { mapFormToRequestBody } from "../helpers/mapFormToRequestBody";

export function SubmitEdit({
  id,
  initialAccountLocations = [],
}: {
  id: string;
  initialAccountLocations?: Array<{ id: string; name?: string }>;
}) {
  const [useUpdateAccount] = useUpdateAccountMutation();
  const [upsertPayerAccountFees] = useUpsertPayerAccountFeesMutation();
  const [upsertBenefitSelectionRules] =
    useUpsertBenefitSelectionRulesMutation();
  const { accountRefetch } = useUserWithAccount();
  const [buttonLoading, setButtonLoading] = useState(false);
  const { current: toastElement } = useContext(ToastContext);
  const {
    getValues,
    formState: { isValid },
    watch,
  } = useFormContext();
  const paymentPlanCustomSettingsField = watch("paymentPlanCustomSettings");
  const navigate = useNavigate();

  const updateHospital = async () => {
    const formValues = getValues();
    const requestBody = await mapFormToRequestBody(formValues, {
      initialAccountLocations,
    });
    setButtonLoading(true);

    try {
      // Update the main account
      await useUpdateAccount({ ...requestBody, id }).unwrap();

      // We always update payer account fees (even if empty array to clear existing fees)
      const formattedPayerAccountFees = (formValues.payerAccountFees || []).map(
        (fee: any) => ({
          payerId: parseInt(fee.payerId, 10),
          type: fee.type || "ClearPatientFee",
          valueInPercentage: parseFloat(fee.valueInPercentage) || 0,
        })
      );

      await upsertPayerAccountFees({
        accountId: id,
        payerAccountFees: formattedPayerAccountFees,
      }).unwrap();

      // CLE-2683 — always sent, so removing the last rule genuinely clears them.
      await upsertBenefitSelectionRules({
        accountId: id,
        rules: (formValues.benefitSelectionRules || []).map((rule: any) => ({
          payerIds: (rule.payerIds || []).map((payerId: any) =>
            parseInt(payerId, 10)
          ),
          planKeywords: rule.planKeywords || [],
          matchKeywords: rule.matchKeywords || [],
          excludeKeywords: rule.excludeKeywords || [],
          tierPreference: rule.tierPreference || null,
          networkStatus: rule.networkStatus || "InNetwork",
        })),
      }).unwrap();

      toastElement?.show({
        severity: "success",
        summary: "Account updated",
        detail: "Account updated successfully",
      });
      setButtonLoading(false);
      navigate("/hospitals");
      accountRefetch();
    } catch (error: any) {
      console.error(error);
      setButtonLoading(false);
      const message =
        error?.data?.message ||
        error?.message ||
        error?.data?.messages?.join(", ") ||
        error?.data?.messages?.[0] ||
        "An error has occurred attempting to update the account";
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: message,
      });
    }
  };

  const submitButtonDIsabled = useMemo(() => {
    if (!isValid) return true;
    if (paymentPlanCustomSettingsField?.length) {
      const isCustomSettingsValid = validaTeCustomPaymentPlanSettings({
        settings: paymentPlanCustomSettingsField,
      });
      if (!isCustomSettingsValid) return true;
    }
    return false;
  }, [isValid, paymentPlanCustomSettingsField]);

  return (
    <ItemBar>
      <div
        data-testid="submit__Hospital"
        className="flex flex-1 justify-content-end"
      >
        <Button
          label="Update Hospital"
          onClick={updateHospital}
          disabled={submitButtonDIsabled}
          loading={buttonLoading}
        />
      </div>
    </ItemBar>
  );
}
